import gql from "graphql-tag";

export const RESEARCH_REQUEST = gql`
    query ResearchRequestQuery($id: ID!) {
        researchRequest(id: $id) {
            entity {
                key {
                    ulid
                }
                name
                entityType
                legalAddress {
                    countryCode
                }
                physicalAddress {
                    countryCode
                }
            }
        }
        entitySchema: jsonSchema(schema: "arachnys.entities.Entity")
    }
`;

export const RESEARCH_REQUEST_ATTACHMENTS = gql`
    query ResearchRequestAttachments(
        $id: ID!
        $isContinuousMonitoring: Boolean
    ) {
        researchRequest(id: $id) {
            fileUploads(isContinuousMonitoring: $isContinuousMonitoring) {
                id
                fileName
                objectKey
                createdAt
                author {
                    firstName
                    lastName
                    username
                }
            }
        }
        permissions {
            task {
                canManageCmFiles
            }
        }
    }
`;

export const VIEW_RESEARCH_REQUEST = gql`
    query ViewResearchRequestQuery($id: ID!) {
        researchRequest(id: $id) {
            entity {
                key {
                    ulid
                }
                name
                entityType
                humanEntityType: entityType(returnMode: LABEL)
                dateOfBirth
                legalAddress {
                    streetAddress
                    city
                    state
                    postalCode
                    countryCode
                }
                physicalAddress {
                    streetAddress
                    city
                    state
                    postalCode
                    countryCode
                }
                addresses {
                    streetAddress
                    city
                    state
                    postalCode
                    countryCode
                }
                additionalProperties
                alternativeNames {
                    localNames
                }
            }
            requester {
                id
            }
            relations {
                fromEntity {
                    name
                    legalAddress {
                        streetAddress
                        city
                        state
                        postalCode
                        countryCode
                    }
                    physicalAddress {
                        streetAddress
                        city
                        state
                        postalCode
                        countryCode
                    }
                    entityType
                    additionalProperties
                    alternativeNames {
                        localNames
                    }
                    dateOfBirth
                }
                type
            }
            requestType
            businessUnit
            monitoringType
            billingCode
            ulid
            comments
            labels
            fileUploads {
                id
                fileName
                objectKey
            }
        }
    }
`;

export const REQUEST_FORM_SCHEMAS = gql`
    query RequestFormSchemasQuery($userId: Int!) {
        entitySchema: jsonSchema(schema: "arachnys.entities.Entity")
        relationSchema: jsonSchema(schema: "arachnys.entities.Relation")
        researchRequestSchema: jsonSchema(
            schema: "arachnys.entities.ResearchRequest"
            includeEnumTypes: true
        )
        customer(userId: $userId) {
            customerType
        }
    }
`;

const RESEARCH_REQUEST_PERMISSIONS_FRAGMENT = gql`
    fragment researchRequestPermissions on TicketingPermissions {
        researchRequest {
            canChangeLabels
        }
    }
`;

export const SEARCH_RESEARCH_REQUESTS = gql`
    query SearchResearchRequestsQuery(
        $name: String
        $count: Int!
        $cursor: String
    ) {
        groupedResearchRequests(name: $name, first: $count, after: $cursor)
            @connection(
                key: "SearchResearchRequestsQuery_groupedResearchRequests"
            ) {
            edges {
                node {
                    id
                    ulid
                    name
                    clientName
                    createdDate
                    countryCode
                    requests {
                        name
                        id
                        ulid
                        createdDate
                        requester {
                            firstName
                            lastName
                        }
                        businessUnit
                        completionDate
                        status
                        canEdit
                        labels
                        entity {
                            key {
                                ulid
                            }
                            linesOfBusiness
                            additionalProperties
                            continuousMonitoring {
                                entityKey {
                                    ulid
                                }
                                pollingFrequency
                                searchEngine
                                status
                                jsonSchema_
                            }
                        }
                    }
                }
            }
            pageInfo {
                endCursor
                hasNextPage
                totalCount
            }
        }
        ticketingPermissions {
            ...researchRequestPermissions
        }
        availableLabels
    }
    ${RESEARCH_REQUEST_PERMISSIONS_FRAGMENT}
`;

export const GET_RESEARCH_REQUEST_LABELS = gql`
    query getResearchRequestLabels {
        ticketingPermissions {
            ...researchRequestPermissions
        }
        availableLabels
    }
    ${RESEARCH_REQUEST_PERMISSIONS_FRAGMENT}
`;

export const SEARCH_ENTITY_NAMES = gql`
    query SearchResearchRequestsNamesQuery($name: String) {
        groupedResearchRequests(name: $name, first: 20)
            @connection(
                key: "SearchResearchRequestsNamesQuery_groupedResearchRequests"
                filter: ["name"]
            ) {
            edges {
                node {
                    id
                    ulid
                    name
                    countryCode
                    requests {
                        id
                        ulid
                        isCompleted
                    }
                }
            }
        }
    }
`;

const BULK_UPLOAD_FILE_FRAGMENT = gql`
    fragment bulkUploadFile on BulkUploadFile {
        fileName
        docstoreId
        fileType
        contentType
        createdAt
        user {
            firstName
            lastName
            username
        }
    }
`;

const BULK_REQUEST_PERMISSIONS_FRAGMENT = gql`
    fragment bulkRequestPermissions on TicketingPermissions {
        bulkUploadRequest {
            canViewAllOpen
            canAssign
            canCreate
            canAccept
            canComplete
            canRelease
        }
    }
`;

const BULK_REQUEST_FRAGMENT = gql`
    fragment bulkRequest on BulkUploadRequest {
        id
        name
        notes
        createdDate
        completionDate
        status
        requester {
            id
            firstName
            lastName
            email
        }
        assignedTo {
            id
            firstName
            lastName
            email
        }
        fileUploads {
            ...bulkUploadFile
        }
    }
    ${BULK_UPLOAD_FILE_FRAGMENT}
`;

export const GET_BULK_UPLOADS = gql`
    query BulkUploadsHistory($name: String, $count: Int!, $cursor: String) {
        bulkUploadRequests(name: $name, first: $count, after: $cursor)
            @connection(key: "BulkUploadQuery_BulkUploadRequests") {
            edges {
                node {
                    ...bulkRequest
                }
            }
            pageInfo {
                totalCount
                endCursor
                hasNextPage
            }
        }
    }
    ${BULK_REQUEST_FRAGMENT}
`;

export const GET_BULK_PERMISSIONS = gql`
    query getBulkRequestPermissions {
        ticketingPermissions {
            ...bulkRequestPermissions
        }
    }
    ${BULK_REQUEST_PERMISSIONS_FRAGMENT}
`;

export const GET_BULK_REQUEST = gql`
    query getBulkRequestDetails($id: ID!) {
        bulkUploadRequest(id: $id) {
            ...bulkRequest
        }
        ticketingPermissions {
            ...bulkRequestPermissions
        }
    }
    ${BULK_REQUEST_FRAGMENT}
    ${BULK_REQUEST_PERMISSIONS_FRAGMENT}
`;

export const GET_BULK_REQUEST_FILES = gql`
    query getBulkRequestDetailsFiles($id: String!, $fileType: String) {
        bulkUploadRequestFiles(bulkUploadRequestId: $id, fileType: $fileType) {
            bulkUploadRequestId
            ...bulkUploadFile
        }
    }
    ${BULK_UPLOAD_FILE_FRAGMENT}
`;
